import { useFileUploader, useInput, useRouting } from '@/hooks'
import { memo, useEffect, useState } from 'react'
import { useCompanyContext } from '../../CompanyDetails.context'
import { Alert, Api } from '@/modules'
import { Button, Flex, Form, Input, Selector, Uploader } from '@/components'
import { ChannelResponse, SearchResponse, UpdateChannelRequest } from '@shared/models'
import { Nbr } from '@/utils/numbers'
import { DialogContainer, DialogContent, DialogFooter, DialogOverlay, ModalHeaderContainer } from './EditChannel.styles'
import { Attention, Close } from '@icon-park/react'
import { Tokens } from '@/assets/tokens'
import { LogoContainer } from '../../CompanyDetails.styles'
import { UploaderViewer } from '@/components/derived'
import { handleWeightChange } from '../../utils'
import { Str } from '@/utils/strings'

const MIN_WEIGHT = 1
const MAX_WEIGHT = 5

export const EditChannelModal = memo(Component)

function Component(): JSX.Element | null {
  const { companyID, queryParams, changeQueryParam } = useRouting()
  const { channels, reloadCompany } = useCompanyContext()

  const [originalMediaLink, setOriginalMediaLink] = useState<string>()
  const [originalWeight, setOriginalWeight] = useState<number>()
  const [originalVehicle, setOriginalVehicle] = useState<string>()
  const [originalTier, setOriginalTier] = useState<string>()
  const [originalSource, setOriginalSource] = useState<number>()
  const [originalPrice, setOriginalPrice] = useState<number>()
  const [originalCountry, setOriginalCountry] = useState<string>()
  const [originalRegion, setOriginalRegion] = useState<string>()
  const [originalState, setOriginalState] = useState<string>()
  const [originalIgnorarVerificacao, setOriginalIgnorarVerificacao] = useState<boolean>(false)
  const [thumbnailChanged, setThumbnailChanged] = useState<boolean | null>(false)
  const [roundedLogoChanged, setRoundedLogoChanged] = useState(false)
  const [ignorarVerificacao, setIgnorarVerificacao] = useState(false)
  const [originalIgnorarCanal, setOriginalIgnorarCanal] = useState<boolean>(false)
  const [ignorarCanal, setIgnorarCanal] = useState(false)

  const { sendAlert } = Alert.Hook()

  const [fetchLogo] = Api.PromiseQuery<string>(`files/:id`)

  const [isSearchingVehicles, searchVehicle, searchedVehicles = []] = Api.Query<SearchResponse[]>(`vehicles/search`)

  const { isRunning: isUpdatingChannel, run: updateChannel } = Api.Command<ChannelResponse>(
    Api.Methods.PATCH,
    '/channels/:id',
  )

  const { binder: nameBinder, reset: resetName } = useInput()
  const { value: mediaLink, binder: mediaLinkBinder, reset: resetMediaLink } = useInput()
  const { value: weight, binder: weightBinder, reset: resetWeight } = useInput()
  const { selectedOption: selectedVehicle, binder: vehicleBinder, reset: resetVehicle } = Selector.Binder()
  const { selectedOption: selectedTier, binder: tierBinder, reset: resetTier } = Selector.Binder()
  const { selectedOption: selectedSource, binder: sourceBinder, reset: resetSource } = Selector.Binder()
  const { value: price, binder: priceBinder, reset: resetPrice } = useInput()
  const { value: country, binder: countryBinder, reset: resetCountry } = useInput()
  const { value: region, binder: regionBinder, reset: resetRegion } = useInput()
  const { value: state, binder: stateBinder, reset: resetState } = useInput()
  const {
    value: roundedLogo,
    bind: roundedLogoBinder,
    setValue: setRoundedLogo,
    reset: resetRoundedLogo,
  } = useFileUploader()
  const { value: thumbnail, bind: thumbnailBinder, setValue: setThumbnail, reset: resetThumbnail } = useFileUploader()

  const tiers = ['SUPER TIER', 'TIER 1', 'TIER 2', 'TIER 3', 'TIER 4', 'TIER 5']
  const dataSources = [
    { text: 'Redes', value: 1 },
    { text: 'Imprensa', value: 2 },
  ]

  useEffect(() => {
    searchVehicle({})
  }, [searchVehicle])

  useEffect(() => {
    if (!queryParams.editChannelId || !channels) return

    const channel = channels.elements.find((p) => p.id === queryParams.editChannelId)
    if (!channel) return

    const weightStr = Str.from(channel.weight)
    const priceStr = Str.from(channel.basePricing)

    resetName(channel.name)
    resetMediaLink(channel.mediaLink)
    setOriginalMediaLink(channel.mediaLink)
    resetWeight(weightStr.val)
    setOriginalWeight(channel.weight)
    resetPrice(priceStr.val)
    setOriginalPrice(channel.basePricing)
    resetCountry(channel.country)
    setOriginalCountry(channel.country)
    resetRegion(channel.region)
    setOriginalRegion(channel.region)
    resetState(channel.state)
    setOriginalState(channel.state)
    setIgnorarVerificacao(channel.ignorarVerificacao ?? false)
    setOriginalIgnorarVerificacao(channel.ignorarVerificacao ?? false)
    setIgnorarCanal(channel.ignorarCanal ?? false)
    setOriginalIgnorarCanal(channel.ignorarCanal ?? false)
    resetVehicle({ text: channel.vehicle!, value: channel.vehicle! })
    setOriginalVehicle(channel.vehicle)
    resetTier({ text: channel.tier!, value: channel.tier! })
    setOriginalTier(channel.tier)
    const sourceOption = dataSources.find((option) => option.value === channel.source)
    resetSource(sourceOption)
    setOriginalSource(channel.source)

    const fetchImage = async (logoID: string, setLogo: (value: any) => void) => {
      if (logoID === 'default-logo-color' || logoID === 'default-logo-gray') return
      const [data] = await fetchLogo({ id: logoID })
      if (data) {
        const [mimePart] = data.split(',')
        const mime = mimePart.split(':')[1].split(';')[0]
        const name = `logo.${mime.split('/')[1]}`
        setLogo({ mime, name, content: data })
      }
    }

    fetchImage(channel.circledLogoID!, setRoundedLogo)
    fetchImage(channel.thumbnailID!, setThumbnail)

    setRoundedLogoChanged(false)
    setThumbnailChanged(false)
  }, [queryParams.editChannelId, channels])

  function handleClose(): void {
    changeQueryParam({ param: 'editChannelId', value: undefined })
    resetRoundedLogo()
    resetThumbnail()
  }

  function onSubmit(): void {
    if (!companyID) return

    if (!selectedSource?.value) {
      sendAlert({ type: 'error', title: 'Campo obrigatório', message: "Campo 'Fonte de Dados' é obrigatório" })
      return
    }

    const input: UpdateChannelRequest = {
      companyID: companyID,
    }

    if (mediaLink !== originalMediaLink) input.mediaLink = mediaLink
    if (Nbr.from(weight).value !== originalWeight) input.weight = Nbr.from(weight).value
    if (selectedVehicle?.value && selectedVehicle.value !== originalVehicle) {
      input.vehicle = String(selectedVehicle.value)
    }
    if (selectedTier?.value && selectedTier.value !== originalTier) {
      input.tier = String(selectedTier.value)
    }
    if (selectedSource?.value && Nbr.from(selectedSource.value).value !== originalSource) {
      input.source = Nbr.from(selectedSource.value).value
    }
    if (Nbr.from(price).value !== originalPrice) input.price = Nbr.from(price).value
    if (country !== originalCountry) input.country = country
    if (region !== originalRegion) input.region = region
    if (state !== originalState) input.state = state
    if (ignorarVerificacao !== originalIgnorarVerificacao) input.ignorarVerificacao = ignorarVerificacao
    if (ignorarCanal !== originalIgnorarCanal) input.ignorarCanal = ignorarCanal

    if (thumbnailChanged && thumbnail) input.thumbnail = thumbnail
    if (roundedLogoChanged && roundedLogo) input.roundedLogo = roundedLogo

    if (Object.keys(input).length === 1) {
      handleClose()
      sendAlert({
        type: 'error',
        title: 'Erro',
        message: 'Nenhuma alteração detectada',
      })
      return
    }

    updateChannel<UpdateChannelRequest>({
      binds: [['id', queryParams.editChannelId!]],
      input,
      onSuccess: () => {
        handleClose()
        reloadCompany()
        sendAlert({
          type: 'success',
          title: 'Sucesso',
          message: 'Canal atualizado com sucesso',
        })
      },
    })
  }

  if (!queryParams.editChannelId) return null

  return (
    <DialogOverlay>
      <DialogContainer>
        <ModalHeaderContainer $type="row" $justify="space-between" $align="stretch" $gap={20} $fit>
          <h1>Atualizar Canal</h1>
          <Button.Root $variant="link" type="button" onClick={handleClose}>
            <Button.Icon value={Close} color={Tokens.ColorTextPrimary} />
          </Button.Root>
        </ModalHeaderContainer>
        <Form onSubmit={onSubmit} controlParam={isUpdatingChannel}>
          <DialogContent>
            <Flex $type="column" $align="stretch" $gap={24} $fit>
              <Input label="Nome do canal" placeholder="Nome" {...nameBinder} required disabled={true} />
              <Flex $type="row" $align="flex-start" $gap={24}>
                <Input label="Endereço web do canal" placeholder="Link do Canal" {...mediaLinkBinder} />
                <Input
                  label="Peso"
                  placeholder="Peso"
                  type="number"
                  maxLength={3}
                  {...weightBinder}
                  required
                  onChange={(value: string) => handleWeightChange(weightBinder, MIN_WEIGHT, MAX_WEIGHT, value)}
                />
              </Flex>
              <Flex $type="row" $align="flex-start" $gap={24}>
                <Selector.Searchable
                  {...vehicleBinder}
                  label="Tipo de mídia"
                  placeholder="Tipo de mídia"
                  options={searchedVehicles.map((vehicle) => ({ ...vehicle }))}
                  onSearch={(searchParam: string): void => {
                    searchVehicle({ name: searchParam })
                  }}
                  loading={isSearchingVehicles}
                  required
                />
                <Selector.Searchable
                  {...tierBinder}
                  label="Tier"
                  placeholder="Tier"
                  options={tiers.map((tier) => ({ text: tier, value: tier }))}
                  onSearch={(): void => {}}
                  loading={isSearchingVehicles}
                  required
                />
              </Flex>
              <Selector.Searchable
                {...sourceBinder}
                label="Fonte de Dados"
                placeholder="Fonte de Dados"
                options={dataSources}
                onSearch={(): void => {}}
                required
              />
              <Flex $type="row" $justify="center" $align="center" $gap={24} $fit>
                <LogoContainer>
                  <Uploader
                    label="Logo circular (.svg 40px/40px)"
                    icon={Attention}
                    iconColor="red"
                    {...roundedLogoBinder}
                    onUpload={(output) => {
                      roundedLogoBinder.onUpload(output)
                      setRoundedLogoChanged(true)
                    }}
                    requiredExtensions={['svg']}
                    required
                  />
                  <UploaderViewer value={roundedLogo} />
                </LogoContainer>
                <LogoContainer>
                  <Uploader
                    label="Logo por extenso (.svg 203px/50px)"
                    icon={Attention}
                    iconColor="red"
                    {...thumbnailBinder}
                    onUpload={(output) => {
                      thumbnailBinder.onUpload(output)
                      setThumbnailChanged(true)
                    }}
                    requiredExtensions={['svg']}
                    required
                  />
                  <UploaderViewer value={thumbnail} />
                </LogoContainer>
              </Flex>
              <Flex $type="row" $justify="space-between" $align="flex-start" $gap={24}>
                <Input
                  preMask="R$"
                  label="Preço base"
                  placeholder="Preço"
                  type="number"
                  maxLength={10}
                  {...priceBinder}
                />
                <Input label="País" placeholder="País" {...countryBinder} />
              </Flex>
              <Flex $type="row" $justify="space-between" $align="flex-start" $gap={24}>
                <Input label="Região" placeholder="Região" {...regionBinder} />
                <Input label="Estado" placeholder="Estado" maxLength={2} {...stateBinder} />
              </Flex>
              <Flex $type="row" $align="center" $gap={8}>
                <input
                  type="checkbox"
                  id="ignorar-verificacao-edit"
                  checked={ignorarVerificacao}
                  onChange={(e) => setIgnorarVerificacao(e.target.checked)}
                />
                <label htmlFor="ignorar-verificacao-edit" style={{ fontSize: 14, cursor: 'pointer' }}>
                  Ignorar verificação de termos (postagens deste canal entram sem verificar conteúdo)
                </label>
              </Flex>
              <Flex $type="row" $align="center" $gap={8}>
                <input
                  type="checkbox"
                  id="ignorar-canal-edit"
                  checked={ignorarCanal}
                  onChange={(e) => setIgnorarCanal(e.target.checked)}
                />
                <label htmlFor="ignorar-canal-edit" style={{ fontSize: 14, cursor: 'pointer' }}>
                  Ignorar canal (postagens deste canal não entram no sistema)
                </label>
              </Flex>
            </Flex>
          </DialogContent>
          <DialogFooter>
            <Flex $type="row" $justify="flex-end" $align="stretch" $gap={20} $fit>
              <Button.Root
                type="button"
                $size={114}
                onClick={handleClose}
                disabled={isUpdatingChannel}
                $loading={isUpdatingChannel}>
                <Button.Text value="Cancelar" />
              </Button.Root>
              <Button.Root
                type="submit"
                $size={114}
                $variant="primary"
                disabled={isUpdatingChannel}
                $loading={isUpdatingChannel}>
                <Button.Text value="Salvar" />
              </Button.Root>
            </Flex>
          </DialogFooter>
        </Form>
      </DialogContainer>
    </DialogOverlay>
  )
}
